// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const { language, changeLanguage, t } = useLanguage();

  useEffect(() => {
    if (location.pathname !== '/') {
      setScrolled(true);
      return;
    }
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 0.7) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownOpen && !event.target.closest('.language-dropdown')) {
        setLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [languageDropdownOpen]);

  const menuItems = [
    { name: t('header.house'), link: '/house' },
    { name: t('header.activities'), link: 'https://www.getyourguide.com/el-gr/-l1806/?cmp=ga&cq_src=google_ads&cq_cmp=20079316703&cq_con=148778598476&cq_term=%CE%B4%CF%81%CE%B1%CF%83%CF%84%CE%B7%CF%81%CE%B9%CE%BF%CF%84%CE%B7%CF%84%CE%B5%CF%83%20%CE%B7%CF%81%CE%B1%CE%BA%CE%BB%CE%B5%CE%B9%CE%BF&cq_med=&cq_plac=&cq_net=g&cq_pos=&cq_plt=gp&campaign_id=20079316703&adgroup_id=148778598476&target_id=kwd-2336044256384&loc_physical_ms=9061578&match_type=b&ad_id=662825414133&keyword=%CE%B4%CF%81%CE%B1%CF%83%CF%84%CE%B7%CF%81%CE%B9%CE%BF%CF%84%CE%B7%CF%84%CE%B5%CF%83%20%CE%B7%CF%81%CE%B1%CE%BA%CE%BB%CE%B5%CE%B9%CE%BF&ad_position=&feed_item_id=&placement=&device=c&partner_id=CD951&gad_source=1&gad_campaignid=20079316703', external: true },
    { name: t('header.beaches'), link: 'https://www.tripadvisor.com.gr/Attractions-g1746299-Activities-c61-t52-Heraklion_Prefecture_Crete.html', external: true },
    { name: t('header.history'), link: 'https://www.greeka.com/crete/history/', external: true },
    { name: t('header.contact'), link: '/contact' }
  ];

  return (
    <header className={`shadow-md w-full top-0 z-50 transition-colors duration-500 ${
      location.pathname === '/' && !scrolled ? 'bg-transparent' : 'bg-black'
    } backdrop-blur-none fixed min-h-0 overflow-visible`}>
      <div className="py-3 sm:py-5 flex items-center w-full px-2 sm:px-6 min-h-0 overflow-visible">
        {/* Left: Logo & Name + Desktop Nav */}
        <div className="flex items-center min-w-max gap-2 sm:gap-3 min-h-0 overflow-visible">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img
              src="/azure_loft_logo_no_bg-removebg-preview copy.png"
              alt="Azure Loft Logo"
              className="h-12 w-12 sm:h-20 sm:w-20 md:h-24 md:w-24 object-contain"
            />
            <span className="text-2xl sm:text-4xl md:text-5xl font-thin text-white tracking-tight select-none font-[Montserrat] whitespace-nowrap uppercase" style={{ fontWeight: 200, fontFamily: 'Montserrat, Poppins, sans-serif', letterSpacing: '0.08em' }}>AZURE LOFT</span>
          </Link>
          {/* Menu Items (Desktop Only) - Right next to logo */}
          <nav className="hidden sm:flex flex-row items-center gap-6 md:gap-16 ml-32">
            {menuItems.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative text-lg md:text-2xl lg:text-3xl font-bold text-white transition-all duration-200 px-2 md:px-6 py-2 md:py-3 rounded-md hover:text-cyan-200 focus:text-cyan-100 after:absolute after:left-0 after:bottom-0 after:w-0 after:h-1 after:bg-gradient-to-r after:from-cyan-200 after:to-blue-200 after:rounded-full after:transition-all after:duration-300 hover:after:w-full"
                  style={{ overflow: 'hidden', letterSpacing: '0.04em' }}
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.link}
                  className="relative text-lg md:text-2xl lg:text-3xl font-bold text-white transition-all duration-200 px-2 md:px-6 py-2 md:py-3 rounded-md hover:text-cyan-200 focus:text-cyan-100 after:absolute after:left-0 after:bottom-0 after:w-0 after:h-1 after:bg-gradient-to-r after:from-cyan-200 after:to-blue-200 after:rounded-full after:transition-all after:duration-300 hover:after:w-full"
                  style={{ overflow: 'hidden', letterSpacing: '0.04em' }}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>
        </div>
        {/* Spacer */}
        <div className="flex-1 min-h-0 overflow-visible" />
        {/* Mobile: Icons + Burger menu (row, always at far right) */}
        <div className="flex sm:hidden items-center gap-3 ml-2 min-h-0 overflow-visible">
          {/* Language Dropdown */}
          <div className="relative language-dropdown min-h-0 overflow-visible">
            <button
              className="flex items-center text-white hover:text-cyan-200 transition-all focus:outline-none"
              onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
            >
              <Globe size={26} />
            </button>
            {languageDropdownOpen && (
              <div className="absolute right-0 mt-2 w-28 md:w-32 bg-white rounded-lg shadow-2xl border-2 border-blue-300 z-[99999]">
                <button onClick={() => { changeLanguage('el'); setLanguageDropdownOpen(false); }} className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition flex items-center justify-between gap-2 ${language === 'el' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'}`}>Ελληνικά <span style={{fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif'}}>🇬🇷</span></button>
                <button onClick={() => { changeLanguage('en'); setLanguageDropdownOpen(false); }} className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition flex items-center justify-between gap-2 ${language === 'en' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'}`}>English <span style={{fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif'}}>🇬🇧</span></button>
                <button onClick={() => { changeLanguage('fr'); setLanguageDropdownOpen(false); }} className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition flex items-center justify-between gap-2 ${language === 'fr' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'}`}>Français <span style={{fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif'}}>🇫🇷</span></button>
                <button onClick={() => { changeLanguage('de'); setLanguageDropdownOpen(false); }} className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition flex items-center justify-between gap-2 ${language === 'de' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'}`}>Deutsch <span style={{fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif'}}>🇩🇪</span></button>
                <button onClick={() => { changeLanguage('it'); setLanguageDropdownOpen(false); }} className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition flex items-center justify-between gap-2 ${language === 'it' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'}`}>Italiano <span style={{fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif'}}>🇮🇹</span></button>
              </div>
            )}
          </div>
          {/* Phone Icon */}
          <a href="tel:+306983446269" className="flex items-center text-white hover:text-cyan-200 transition-all">
            <Phone size={24} />
          </a>
          {/* Burger menu icon (Mobile Only, always last) */}
          <button
            className="ml-2 flex items-center text-white focus:outline-none z-50"
            onClick={toggleMenu}
            aria-label="Open menu"
          >
            {isOpen ? <X size={36} /> : <Menu size={36} />}
          </button>
        </div>
        {/* Right: Language & Phone (hidden in mobile, shown in menu) */}
        <div className="hidden sm:flex items-center gap-4 md:gap-6 min-w-max flex-shrink-0">
          <div className="relative language-dropdown">
            <button 
              className="flex items-center text-white hover:text-cyan-200 transition-all focus:outline-none"
              onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
            >
              <Globe size={28} className="mr-1 md:mr-2" />
              <span className="text-base md:text-xl font-bold">{language.toUpperCase()}</span>
            </button>
            {languageDropdownOpen && (
              <div className="absolute right-0 mt-2 w-28 md:w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <button
                  onClick={() => {
                    changeLanguage('el');
                    setLanguageDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition ${
                    language === 'el' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  Ελληνικά
                </button>
                <button
                  onClick={() => {
                    changeLanguage('en');
                    setLanguageDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition ${
                    language === 'en' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => {
                    changeLanguage('fr');
                    setLanguageDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition ${
                    language === 'fr' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  Français
                </button>
                <button
                  onClick={() => {
                    changeLanguage('de');
                    setLanguageDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition ${
                    language === 'de' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  Deutsch
                </button>
                <button
                  onClick={() => {
                    changeLanguage('it');
                    setLanguageDropdownOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 transition ${
                    language === 'it' ? 'bg-blue-100 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  Italiano
                </button>
              </div>
            )}
          </div>
          <a href="tel:+306983446269" className="flex items-center gap-1 md:gap-2 text-white hover:text-cyan-200 transition-all">
            <Phone size={22} />
            <span className="text-base md:text-2xl font-bold select-all">{t('header.phone')}</span>
          </a>
        </div>
        {/* Mobile Menu Drawer */}
        {isOpen && (
          <div className="fixed inset-0 bg-black/80 z-40 flex flex-col items-center justify-start pt-24 px-4 sm:hidden animate-fade-in">
            <nav className="flex flex-col gap-6 w-full max-w-xs mx-auto">
              {menuItems.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-2xl font-bold text-white py-3 px-4 rounded-lg hover:bg-cyan-900/30 transition"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.link}
                    className="text-2xl font-bold text-white py-3 px-4 rounded-lg hover:bg-cyan-900/30 transition"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              {/* Αφαιρώ το language-dropdown από εδώ */}
              <a href="tel:+306983446269" className="flex items-center gap-2 text-white hover:text-cyan-200 transition-all">
                <Phone size={24} />
                <span className="text-lg font-bold select-all">{t('header.phone')}</span>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
